import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { ArrowLeft, Save, User, Music as MusicIcon, Heart, Target, Lock } from 'lucide-react';
import { MUSIC_GENRES, FAVORITE_ARTISTS, WELLNESS_GOALS, getFilteredArtists } from '../translations';
import { toast } from 'sonner';

export default function UserProfile({ user }) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const [profile, setProfile] = useState({
    music_preferences: user?.music_preferences || [],
    artist_preferences: user?.artist_preferences || [],
    wellness_goals: user?.wellness_goals || []
  });

  const toggleSelection = (category, value) => {
    setProfile(prev => ({
      ...prev,
      [category]: prev[category].includes(value)
        ? prev[category].filter(v => v !== value)
        : [...prev[category], value]
    }));
  };

  const handleSave = async () => {
    try {
      await axios.patch(`${API}/auth/update-profile`, profile);
      toast.success('Profile updated successfully!');
      setEditing(false);
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new !== passwordData.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordData.new.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.current,
        new_password: passwordData.new
      });
      toast.success('Password changed successfully!');
      setShowPasswordChange(false);
      setPasswordData({ current: '', new: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="h-screen flex flex-col bg-gradient-to-b from-white via-stone-50 to-stone-100 overflow-hidden">
      {/* Header */}
      <div className="flex-none px-4 py-3 bg-white border-b border-stone-200 flex items-center justify-between">
        <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-warm-charcoal/60 hover:text-warm-charcoal">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Back</span>
        </button>
        <h1 className="text-lg font-semibold text-warm-charcoal" style={{ fontFamily: 'Playfair Display, serif' }}>
          My Profile
        </h1>
        <button onClick={() => setEditing(!editing)} className="text-sm text-hunter-green font-medium">
          {editing ? 'Cancel' : 'Edit'}
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {/* User Info */}
        <div className="bg-white p-4 rounded-xl border border-stone-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-full bg-hunter-green/10 flex items-center justify-center">
              <User className="w-6 h-6 text-hunter-green" />
            </div>
            <div>
              <p className="font-semibold text-warm-charcoal">{user?.name}</p>
              <p className="text-xs text-warm-charcoal/60">{user?.email}</p>
            </div>
          </div>
        </div>

        {/* Change Password */}
        <div className="bg-white p-4 rounded-xl border border-stone-200">
          <button
            onClick={() => setShowPasswordChange(!showPasswordChange)}
            className="flex items-center gap-2 w-full text-left"
            style={{ touchAction: 'manipulation' }}
          >
            <Lock className="w-4 h-4 text-hunter-green" />
            <span className="text-sm font-semibold text-warm-charcoal">Change Password</span>
          </button>
          {showPasswordChange && (
            <div className="mt-3 space-y-3">
              <input
                type="password"
                value={passwordData.current}
                onChange={(e) => setPasswordData(p => ({ ...p, current: e.target.value }))}
                placeholder="Current password"
                className="w-full px-3 py-2.5 rounded-lg border border-stone-200 text-sm focus:border-hunter-green focus:ring-1 focus:ring-hunter-green/20 outline-none"
                data-testid="current-password"
              />
              <input
                type="password"
                value={passwordData.new}
                onChange={(e) => setPasswordData(p => ({ ...p, new: e.target.value }))}
                placeholder="New password (min 6 characters)"
                className="w-full px-3 py-2.5 rounded-lg border border-stone-200 text-sm focus:border-hunter-green focus:ring-1 focus:ring-hunter-green/20 outline-none"
                data-testid="new-password"
              />
              <input
                type="password"
                value={passwordData.confirm}
                onChange={(e) => setPasswordData(p => ({ ...p, confirm: e.target.value }))}
                placeholder="Confirm new password"
                className="w-full px-3 py-2.5 rounded-lg border border-stone-200 text-sm focus:border-hunter-green focus:ring-1 focus:ring-hunter-green/20 outline-none"
                data-testid="confirm-password"
              />
              <button
                onClick={handleChangePassword}
                disabled={changingPassword || !passwordData.current || !passwordData.new}
                className="w-full py-2.5 rounded-lg bg-hunter-green text-white text-sm font-medium hover:bg-hunter-green/90 disabled:opacity-50 transition-colors"
                data-testid="change-password-btn"
              >
                {changingPassword ? 'Updating...' : 'Update Password'}
              </button>
            </div>
          )}
        </div>

        {/* Music Preferences */}
        <div className="bg-white p-4 rounded-xl border border-stone-200">
          <div className="flex items-center gap-2 mb-3">
            <MusicIcon className="w-4 h-4 text-hunter-green" />
            <h3 className="font-semibold text-sm">Music Genres</h3>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {MUSIC_GENRES.map((genre) => (
              <button
                key={genre.value}
                onClick={() => editing && toggleSelection('music_preferences', genre.value)}
                disabled={!editing}
                className={`p-2 rounded-lg text-xs font-medium transition-all ${
                  profile.music_preferences.includes(genre.value)
                    ? 'bg-hunter-green text-white'
                    : editing
                    ? 'bg-stone-50 hover:bg-stone-100 text-warm-charcoal/70 border border-stone-200'
                    : 'bg-stone-50 text-warm-charcoal/40'
                }`}
              >
                {genre.label}
              </button>
            ))}
          </div>
        </div>

        {/* Artist Preferences */}
        <div className="bg-white p-4 rounded-xl border border-stone-200">
          <div className="flex items-center gap-2 mb-3">
            <Heart className="w-4 h-4 text-hunter-green" />
            <h3 className="font-semibold text-sm">Favorite Artists</h3>
          </div>
          <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
            {getFilteredArtists(profile.music_preferences).map((artist) => (
              <button
                key={artist.value}
                onClick={() => editing && toggleSelection('artist_preferences', artist.value)}
                disabled={!editing}
                className={`p-2 rounded-lg text-[10px] font-medium transition-all ${
                  profile.artist_preferences.includes(artist.value)
                    ? 'bg-hunter-green text-white'
                    : editing
                    ? 'bg-stone-50 hover:bg-stone-100 text-warm-charcoal/70 border border-stone-200'
                    : 'bg-stone-50 text-warm-charcoal/40'
                }`}
              >
                {artist.label}
              </button>
            ))}
          </div>
        </div>

        {/* Wellness Goals */}
        <div className="bg-white p-4 rounded-xl border border-stone-200">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-4 h-4 text-hunter-green" />
            <h3 className="font-semibold text-sm">Wellness Goals</h3>
          </div>
          <div className="grid grid-cols-1 gap-2">
            {WELLNESS_GOALS.map((goal) => (
              <button
                key={goal.value}
                onClick={() => editing && toggleSelection('wellness_goals', goal.value)}
                disabled={!editing}
                className={`p-2 rounded-lg text-xs font-medium transition-all text-left ${
                  profile.wellness_goals.includes(goal.value)
                    ? 'bg-hunter-green text-white'
                    : editing
                    ? 'bg-stone-50 hover:bg-stone-100 text-warm-charcoal/70 border border-stone-200'
                    : 'bg-stone-50 text-warm-charcoal/40'
                }`}
              >
                {goal.label}
              </button>
            ))}
          </div>
        </div>

        {/* Save Button */}
        {editing && (
          <button
            onClick={handleSave}
            className="w-full flex items-center justify-center gap-2 py-3 bg-hunter-green text-white rounded-xl font-semibold shadow-lg"
          >
            <Save className="w-4 h-4" />
            <span>Save Changes</span>
          </button>
        )}
      </div>
    </div>
  );
}
